import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { apiFetch } from '../api/http';
import { getSupabaseClient } from './supabase';

type ClientOption = { supabase?: SupabaseClient };

export const SUGGESTION_CATEGORY_OPTIONS = [
  { value: 'bug', label: 'Bug', description: 'Something is not working as expected.' },
  { value: 'question', label: 'Question', description: 'You need clarification or help.' },
  { value: 'suggestion', label: 'Suggestion', description: 'An idea to improve the app.' },
  { value: 'data_issue', label: 'Data issue', description: 'Information is missing or incorrect.' },
] as const;

export type SuggestionCategory = typeof SUGGESTION_CATEGORY_OPTIONS[number]['value'];

export function formatSuggestionCategory(category: SuggestionCategory | null | undefined): string {
  return SUGGESTION_CATEGORY_OPTIONS.find((option) => option.value === category)?.label ?? 'Uncategorized';
}

export type SuggestionReply = {
  id: string;
  body: string;
  sender_profile_id: string;
  sender_name: string | null;
  sender_role: 'admin' | 'author';
  created_at: string;
};

export type SuggestionAttachment = {
  attachment_id: string;
  storage_bucket: string;
  storage_path: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  signed_url?: string;
  preview_url?: string | null;
};

type SuggestionAttachmentUploadSession = SuggestionAttachment & {
  client_index: number;
  upload_token: string;
};

export type SuggestionRow = {
  suggestion_id: string;
  author_profile_id: string;
  subject: string | null;
  body: string;
  category: SuggestionCategory | null;
  status: 'new' | 'read' | 'in_progress' | 'resolved' | 'deferred' | 'dismissed';
  response: string | null;
  responded_at: string | null;
  responded_by: string | null;
  author_archived_at: string | null;
  reply_thread: SuggestionReply[];
  attachments: SuggestionAttachment[];
  created_at: string;
  updated_at: string;
  author?: {
    id: string;
    full_name: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
};

export function isInactiveSuggestionStatus(status: SuggestionRow['status']): boolean {
  return status === 'resolved' || status === 'deferred' || status === 'dismissed';
}

/** Submit a new suggestion (any authenticated user). */
export async function createSuggestion(
  params: {
    authorProfileId: string;
    category: SuggestionCategory;
    subject?: string | null;
    body: string;
    screenshots?: File[];
  },
  options?: ClientOption,
): Promise<{ data: SuggestionRow | null; error: Error | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const suggestionId = crypto.randomUUID();
  let uploadedSessions: SuggestionAttachmentUploadSession[] = [];
  try {
    const screenshots = params.screenshots ?? [];
    if (screenshots.length > 5) throw new Error('Attach up to 5 screenshots per suggestion.');
    if (screenshots.length) {
      const sessionResponse = await apiFetch('/dashboard/suggestion-attachments/upload-sessions', {
        method: 'POST',
        body: JSON.stringify({
          suggestionId,
          files: screenshots.map((file, clientIndex) => ({
            clientIndex,
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
          })),
        }),
      });
      const sessionPayload = await sessionResponse.json().catch(() => null);
      if (!sessionResponse.ok) {
        throw new Error(sessionPayload?.message || sessionPayload?.error || 'Could not prepare screenshot uploads.');
      }
      uploadedSessions = Array.isArray(sessionPayload?.items) ? sessionPayload.items : [];
      if (uploadedSessions.length !== screenshots.length) {
        throw new Error('The screenshot upload session did not include every selected file.');
      }
      const sessionByClientIndex = new Map<number, SuggestionAttachmentUploadSession>();
      uploadedSessions.forEach((session) => sessionByClientIndex.set(Number(session.client_index), session));
      const uploads = await Promise.allSettled(screenshots.map(async (file, index) => {
        const session = sessionByClientIndex.get(index);
        if (!session) throw new Error(`The upload session for ${file.name} is missing.`);
        const { error } = await supabase.storage
          .from(session.storage_bucket)
          .uploadToSignedUrl(session.storage_path, session.upload_token, file, {
            cacheControl: '3600',
            contentType: file.type,
            upsert: false,
          });
        if (error) throw error;
      }));
      const failedUpload = uploads.find((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (failedUpload) {
        throw failedUpload.reason instanceof Error
          ? failedUpload.reason
          : new Error('A screenshot could not be uploaded.');
      }
    }
    const response = await apiFetch('/dashboard/suggestions', {
      method: 'POST',
      body: JSON.stringify({
        suggestionId,
        authorProfileId: params.authorProfileId,
        subject: params.subject?.trim() || null,
        body: params.body.trim(),
        category: params.category,
        attachments: uploadedSessions,
      }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new Error(payload?.message || payload?.error || 'Failed to send suggestion');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('dashboard:notifications-refresh'));
    }
    return { data: payload?.data as SuggestionRow, error: null };
  } catch (error) {
    if (uploadedSessions.length) {
      await apiFetch('/dashboard/suggestion-attachments/cleanup', {
        method: 'POST',
        body: JSON.stringify({
          suggestionId,
          paths: uploadedSessions.map((session) => session.storage_path),
        }),
      }).catch(() => undefined);
    }
    return { data: null, error: error instanceof Error ? error : new Error('Failed to send suggestion') };
  }
}

export async function fetchSuggestionAttachments(suggestionId: string): Promise<SuggestionAttachment[]> {
  const response = await apiFetch(`/dashboard/suggestions/${encodeURIComponent(suggestionId)}/attachments`);
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || 'Could not load screenshots.');
  }
  return Array.isArray(payload?.items) ? payload.items as SuggestionAttachment[] : [];
}

export async function addSuggestionScreenshots(
  suggestionId: string,
  screenshots: File[],
  options?: ClientOption,
): Promise<SuggestionRow> {
  const supabase = options?.supabase ?? getSupabaseClient();
  if (!screenshots.length) throw new Error('Choose at least one screenshot.');
  if (screenshots.length > 5) throw new Error('Attach up to 5 screenshots per suggestion.');
  let uploadedSessions: SuggestionAttachmentUploadSession[] = [];
  try {
    const sessionResponse = await apiFetch('/dashboard/suggestion-attachments/upload-sessions', {
      method: 'POST',
      body: JSON.stringify({
        suggestionId,
        files: screenshots.map((file, clientIndex) => ({
          clientIndex,
          fileName: file.name,
          contentType: file.type,
          sizeBytes: file.size,
        })),
      }),
    });
    const sessionPayload = await sessionResponse.json().catch(() => null);
    if (!sessionResponse.ok) {
      throw new Error(sessionPayload?.message || sessionPayload?.error || 'Could not prepare screenshot uploads.');
    }
    uploadedSessions = Array.isArray(sessionPayload?.items) ? sessionPayload.items : [];
    if (uploadedSessions.length !== screenshots.length) {
      throw new Error('The screenshot upload session did not include every selected file.');
    }

    const sessionsByIndex = new Map<number, SuggestionAttachmentUploadSession>();
    uploadedSessions.forEach((session) => sessionsByIndex.set(Number(session.client_index), session));
    const uploads = await Promise.allSettled(screenshots.map(async (file, index) => {
      const session = sessionsByIndex.get(index);
      if (!session) throw new Error(`The upload session for ${file.name} is missing.`);
      const { error } = await supabase.storage
        .from(session.storage_bucket)
        .uploadToSignedUrl(session.storage_path, session.upload_token, file, {
          cacheControl: '3600',
          contentType: file.type,
          upsert: false,
        });
      if (error) throw error;
    }));
    const failedUpload = uploads.find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failedUpload) {
      throw failedUpload.reason instanceof Error
        ? failedUpload.reason
        : new Error('A screenshot could not be uploaded.');
    }

    const finalizeResponse = await apiFetch(`/dashboard/suggestions/${encodeURIComponent(suggestionId)}/attachments`, {
      method: 'POST',
      body: JSON.stringify({ attachments: uploadedSessions }),
    });
    const finalizePayload = await finalizeResponse.json().catch(() => null);
    if (!finalizeResponse.ok) {
      throw new Error(finalizePayload?.message || finalizePayload?.error || 'Could not attach the screenshots.');
    }
    return finalizePayload?.data as SuggestionRow;
  } catch (error) {
    if (uploadedSessions.length) {
      await apiFetch('/dashboard/suggestion-attachments/cleanup', {
        method: 'POST',
        body: JSON.stringify({
          suggestionId,
          paths: uploadedSessions.map((session) => session.storage_path),
        }),
      }).catch(() => undefined);
    }
    throw error instanceof Error ? error : new Error('Could not attach the screenshots.');
  }
}

/** Fetch suggestions authored by the current user (ordered newest first). */
export async function fetchMySuggestions(
  profileId: string,
  options?: ClientOption,
): Promise<{ data: SuggestionRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('suggestions')
    .select('*')
    .eq('author_profile_id', profileId)
    .order('created_at', { ascending: false })
    .limit(50);
  return { data: (data ?? []) as SuggestionRow[], error };
}

/** Fetch ALL suggestions (super-admin only – RLS enforces access). */
export async function fetchAllSuggestions(
  options?: ClientOption & { status?: string; category?: SuggestionCategory },
): Promise<{ data: SuggestionRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  let query = supabase
    .from('suggestions')
    .select(
      `*,
       author:author_profile_id(id, full_name, first_name, last_name, email)`,
    )
    .order('created_at', { ascending: false })
    .limit(100);

  if (options?.status) {
    query = query.eq('status', options.status);
  }
  if (options?.category) {
    query = query.eq('category', options.category);
  }

  const { data, error } = await query;

  const rows = Array.isArray(data)
    ? (data as any[]).map((row) => {
        const author = Array.isArray(row.author) ? row.author[0] ?? null : row.author ?? null;
        return { ...row, author } as SuggestionRow;
      })
    : [];

  return { data: rows, error };
}

/** Count unread (new) suggestions (super-admin only). */
export async function countNewSuggestions(
  options?: ClientOption,
): Promise<number> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { count, error } = await supabase
    .from('suggestions')
    .select('*', { count: 'planned', head: true })
    .eq('status', 'new');
  if (error) return 0;
  return typeof count === 'number' ? count : 0;
}

/** Update a suggestion (super-admin: respond, change status). */
export async function updateSuggestion(
  suggestionId: string,
  updates: {
    status?: SuggestionRow['status'];
    category?: SuggestionCategory;
    response?: string | null;
    responded_by?: string;
  },
): Promise<{ data: SuggestionRow | null; error: Error | null }> {
  try {
    const response = await apiFetch(`/dashboard/suggestions/${encodeURIComponent(suggestionId)}/admin`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(payload?.error || 'Failed to update suggestion');
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('dashboard:notifications-refresh'));
    }
    return { data: payload?.data as SuggestionRow, error: null };
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to update suggestion');
  }
}

export async function replyToSuggestion(
  suggestionId: string,
  body: string,
): Promise<SuggestionRow> {
  const response = await apiFetch(`/dashboard/suggestions/${encodeURIComponent(suggestionId)}/replies`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error || 'Failed to send suggestion reply');
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('dashboard:notifications-refresh'));
  }
  return payload?.data as SuggestionRow;
}

export async function updateOwnSuggestion(
  suggestionId: string,
  updates: {
    subject?: string | null;
    body?: string;
    category?: SuggestionCategory;
    archived?: boolean;
  },
): Promise<SuggestionRow> {
  const response = await apiFetch(`/dashboard/suggestions/${encodeURIComponent(suggestionId)}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error || 'Failed to update suggestion');
  }
  return payload?.data as SuggestionRow;
}

/** Subscribe to realtime changes on suggestions. */
export function subscribeSuggestions(params: {
  supabase?: SupabaseClient;
  onChange: () => void;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  let closed = false;
  let channel: ReturnType<typeof supabase.channel> | null = null;

  const unsubscribeChannel = () => {
    if (!channel) return;
    try {
      supabase.removeChannel(channel);
    } catch {
      /* noop */
    }
    channel = null;
  };

  const subscribeVisible = () => {
    if (closed || channel) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    channel = supabase
      .channel('suggestions-rt')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'suggestions' },
        params.onChange,
      )
      .subscribe();
  };

  const handleVisibilityChange = () => {
    if (typeof document === 'undefined') return;
    if (document.hidden) {
      unsubscribeChannel();
      return;
    }
    subscribeVisible();
    params.onChange();
  };

  subscribeVisible();
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  return () => {
    closed = true;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    unsubscribeChannel();
  };
}
